import { Link } from 'react-router-dom'
import {
  ExpandableIdentityDetails,
  type IdentityDetailItem,
} from '@/components/business/expandable-identity-details'
import { Icon } from '@/components/business/icon'
import { NotificationCenterBell } from '@/features/notification/components/NotificationCenterBell'

const APP_SHELL_MENU_ICON_SRC = '/app-shell/icons/sidebar-menu.svg'

interface AppHeaderProps {
  title?: string
  /** 标题内部链接 */
  titleHref?: string
  /** 顶部标题下方的提示文案 */
  subtitle?: string
  /** 点击副标题后展示的完整详情 */
  subtitleDetails?: IdentityDetailItem[]
  /** 当前侧边栏是否已隐藏 */
  sidebarCollapsed?: boolean
  /** 顶部按钮触发侧边栏展开/收起 */
  onToggleSidebar?: () => void
  /** 返回上一页, 传入时在菜单按钮左侧显示返回箭头 (子页面统一返回入口) */
  onBack?: () => void
}

/**
 * 豆包式顶部栏: 左侧保留返回/侧栏开关, 中间内容按页面需要显示。
 */
export function AppHeader({
  title,
  titleHref,
  subtitle,
  subtitleDetails,
  sidebarCollapsed = false,
  onToggleSidebar,
  onBack,
}: AppHeaderProps) {
  return (
    <header className="app-shell-header-surface sticky top-0 z-30 flex h-16 shrink-0 items-center justify-center border-b px-4">
      {(onBack || onToggleSidebar) && (
        <div className="absolute top-1/2 left-[20px] flex -translate-y-1/2 items-center gap-1">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="text-on-surface focus-visible:ring-primary/25 flex h-7 w-7 items-center justify-center rounded-sm text-[18px] transition-colors hover:bg-black/[0.04] focus-visible:ring-2 focus-visible:outline-none"
              aria-label="返回"
              title="返回"
            >
              <Icon name="arrow_back" />
            </button>
          )}
          {onToggleSidebar && (
            <button
              type="button"
              onClick={onToggleSidebar}
              className="focus-visible:ring-primary/25 flex h-7 w-7 items-center justify-center rounded-sm transition-colors hover:bg-black/[0.04] focus-visible:ring-2 focus-visible:outline-none"
              aria-label={sidebarCollapsed ? '展开侧边栏' : '隐藏侧边栏'}
              title={sidebarCollapsed ? '展开侧边栏' : '隐藏侧边栏'}
            >
              <img src={APP_SHELL_MENU_ICON_SRC} alt="" className="h-5 w-5" aria-hidden="true" />
            </button>
          )}
        </div>
      )}

      {(title || subtitle) && (
        <div className="absolute top-1/2 left-1/2 flex max-w-[calc(100vw-9rem)] min-w-0 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center text-center md:max-w-[70vw]">
          {title && (
            <h1 className="max-w-full truncate text-[16px] leading-[22px] font-normal text-black">
              {titleHref ? (
                <Link
                  to={titleHref}
                  className="block max-w-full truncate rounded-sm text-[#007aff] underline-offset-2 transition-opacity hover:underline focus-visible:ring-2 focus-visible:ring-[#007aff]/25 focus-visible:outline-none active:opacity-60"
                >
                  {title}
                </Link>
              ) : (
                title
              )}
            </h1>
          )}
          {subtitle && (
            <ExpandableIdentityDetails
              key={`${titleHref ?? ''}-${subtitle}`}
              summary={subtitle}
              details={subtitleDetails}
            />
          )}
        </div>
      )}

      <div className="absolute right-4 flex items-center">
        <NotificationCenterBell />
      </div>
    </header>
  )
}
